import {
  File as FileIcon,
  FileSpreadsheet,
  FileText,
  Image as ImageIcon,
  Presentation,
  Trash2,
} from 'lucide-react';
import { documentKind, type DocumentKind, type DocumentRow } from '@jeraisonne/documents';
import { documentsClient, useDocumentThumbnailUrl } from '@/lib/documents/queries';
import { openDocumentUrl } from '@/lib/documents/open';

const KIND_ICON: Record<DocumentKind, typeof FileIcon> = {
  image: ImageIcon,
  pdf: FileText,
  word: FileText,
  text: FileText,
  sheet: FileSpreadsheet,
  slides: Presentation,
  other: FileIcon,
};

/**
 * Une ligne de fichier. Le nom ouvre le fichier : l'URL signée (5 min) n'est
 * demandée qu'AU CLIC, donc une liste de plusieurs centaines de fichiers ne fait
 * aucun appel de signature au chargement. La miniature d'une image (liste courte
 * seulement, `showThumbnail`) fait exception.
 *
 * Mobile-first : la ligne entière est une cible de tap ≥ 44 px, la suppression
 * aussi ; le nom se tronque, jamais de débordement à 375 px.
 */
export function DocumentItem({
  doc,
  meta,
  showThumbnail = false,
  canDelete,
  deleting = false,
  onDelete,
  onError,
}: {
  doc: DocumentRow;
  meta: string;
  showThumbnail?: boolean;
  canDelete: boolean;
  deleting?: boolean;
  onDelete: (doc: DocumentRow) => void;
  onError: (message: string) => void;
}) {
  const kind = documentKind(doc.mimeType);
  const Icon = KIND_ICON[kind];
  const thumb = useDocumentThumbnailUrl(doc.storagePath, showThumbnail && kind === 'image');

  async function handleOpen() {
    try {
      await openDocumentUrl(() => documentsClient.signedUrl(doc.storagePath));
    } catch {
      onError("Impossible d'ouvrir ce fichier. Il a peut-être été supprimé.");
    }
  }

  return (
    <li className="border-ink-100 flex items-center gap-2 rounded-2xl border bg-white p-2 pl-3">
      <button
        type="button"
        onClick={() => void handleOpen()}
        aria-label={`Ouvrir ${doc.fileName}`}
        className="flex min-h-[2.75rem] min-w-0 flex-1 items-center gap-3 rounded-xl text-left transition-colors active:scale-[0.98]"
      >
        {thumb.data ? (
          <img
            src={thumb.data}
            alt=""
            loading="lazy"
            className="size-10 shrink-0 rounded-lg object-cover"
          />
        ) : (
          <span className="bg-ink-50 text-ink-500 flex size-10 shrink-0 items-center justify-center rounded-lg">
            <Icon className="size-5" aria-hidden />
          </span>
        )}
        <span className="min-w-0 flex-1">
          <span className="text-ink-900 block truncate text-sm font-semibold">{doc.fileName}</span>
          <span className="text-ink-500 mt-0.5 block truncate text-xs">{meta}</span>
        </span>
      </button>
      {canDelete && (
        <button
          type="button"
          onClick={() => onDelete(doc)}
          disabled={deleting}
          aria-label={`Supprimer ${doc.fileName}`}
          title="Supprimer"
          className="text-ink-400 hover:text-ink-700 flex min-h-[2.75rem] min-w-[2.75rem] shrink-0 items-center justify-center rounded-xl transition-colors active:scale-95 disabled:opacity-50"
        >
          <Trash2 className="size-4" aria-hidden />
        </button>
      )}
    </li>
  );
}
